import React, { useEffect, useRef, useState } from "react"
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledAccordion,
} from "reactstrap"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  booleanSorter,
  getLottieLoadingOptions,
  getLottieNothingFound,
  showToast,
  stringSorter,
  numberSorter,
  defaultPageSizeOptions,
} from "utils/general"
import { Slider, Switch, Table as AntdTable, Form as AntdForm, Input as AntdInput, Select } from "antd"
import Box from "@mui/material/Box"
import {
  geocodeGetCoordinates,
  getNavigilSettings,
  setNavigilSettings,
} from "helpers/backend_helper"
import Lottie from "react-lottie"
import DefaultMap from "components/GoogleMap/map"
import withRouter from "components/Common/withRouter"
import { tableDefaultHeight } from "constants/layout"

const NavigilSettings = props => {
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [fetching, setFetching] = useState(false)

  const [settings, setSettings] = useState(null)
  const [beaconModal, setBeaconModal] = useState(false)
  const [contactModal, setContactModal] = useState(false)

  const [funcModes, setFuncModes] = useState([])
  const [languages, setLanguages] = useState([])
  const [profiles, setProfiles] = useState([])
  const [updateIntervals, setUpdateIntervals] = useState([])
  const [userModes, setUserModes] = useState([])
  const [timezones, setTimezones] = useState([])
  const [callCenters, setCallCenters] = useState([])
  const [callList, setCallList] = useState([])
  const [homeBeacons, setHomeBeacons] = useState([])
  const [geofences, setGeofences] = useState([])

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (props.deviceId) {
      //console.log("ID changed: ", props.deviceId)
      //getSettings()
    }
  }, [props.deviceId])

  // Get Navigil settings
  const getSettings = () => {
    if (!loading && props.deviceId) {
      setLoading(true)
      getNavigilSettings(abortRef.current, props.deviceId)
        .then(result => {
          //console.log("SETTINGS: ", result.settings)
          if (result.statusCode == 200 && result.settings) {
            setSettings(result.settings)

            if (result.settings?.deviceCallList?.callList) {
              setCallList(result.settings.deviceCallList.callList)
            }

            if (result.settings?.homeBeacons) {
              setHomeBeacons(result.settings.homeBeacons)
            }

            if (result.settings?.geofences) {
              let temp = structuredClone(result.settings.geofences)
              for (const element of temp) {
                element.coordinates = JSON.stringify(element.coordinates)
              }

              setGeofences(temp)
            }

            if (
              result.settings?.generalSettings?.functionalityMode?.available
            ) {
              setFuncModes(
                result.settings.generalSettings.functionalityMode.available
              )
            }

            if (result.settings?.generalSettings?.language?.available) {
              setLanguages(result.settings.generalSettings.language.available)
            }

            if (result.settings?.generalSettings?.profile?.available) {
              setProfiles(result.settings.generalSettings.profile.available)
            }

            if (result.settings?.generalSettings?.timezone?.available) {
              setTimezones(result.settings.generalSettings.timezone.available)
            }

            if (result.settings?.generalSettings?.userMode?.available) {
              setUserModes(result.settings.generalSettings.userMode.available)
            }

            if (
              result.settings?.generalSettings?.statusUpdateInterval?.available
            ) {
              setUpdateIntervals(
                result.settings.generalSettings.statusUpdateInterval.available
              )
            }

            if (result.settings?.deviceCallList?.availableCallCenters) {
              let centers = [
                { name: props.t("No selection"), number: "", id: 0 },
              ]
              for (const cc of result.settings.deviceCallList.availableCallCenters) {
                centers.push(cc)
              }
              setCallCenters(centers)
            }
          } else {
            setSettings(null)
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
          setSettings(null)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Get coordinates
  const getCoordinates = address => {
    if (address && !fetching) {
      setFetching(true)
      geocodeGetCoordinates(abortRef.current, address)
        .then(result => {
          console.log("COORDS: ", result)
          if (result.statusCode == 200 && result.coordinates) {
            result.coordinates.lat = result.coordinates.lat.toFixed(6)
            result.coordinates.lng = result.coordinates.lng.toFixed(6)

            beaconValidation.setFieldValue(
              "latitude",
              "" + result.coordinates.lat
            )
            beaconValidation.setFieldValue(
              "longitude",
              "" + result.coordinates.lng
            )
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setFetching(false)
        })
    }
  }

  // Set default settings
  const setDefaultValues = () => {
    if (settings) {
      validation.setFieldValue("dataRetentionDays", 90)
      validation.setFieldValue("locationAllowed", true)
      validation.setFieldValue("emergencyLocationAllowed", true)
      validation.setFieldValue("volume", 8)
      validation.setFieldValue("functionalityMode", 1)
      validation.setFieldValue("language", 1)
      validation.setFieldValue("profile", 136)
      validation.setFieldValue("timezone", 20)
      validation.setFieldValue("userMode", 2)
      validation.setFieldValue("statusUpdateInterval", 360)
      validation.setFieldValue("answerMachineAvoidance", true)
      validation.setFieldValue("whitelistingEnabled", false)
      validation.setFieldValue("ringingTimeout", 60)
      validation.setFieldValue("callCenterId", 38)
    }
  }

  // Update validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: settings?.assisteeData?.firstName || "",
      lastName: settings?.assisteeData?.lastName || "",
      dataRetentionDays: settings?.devicePermissions?.dataRetentionDays || 30,
      locationAllowed: settings?.devicePermissions?.locationAllowed || false,
      emergencyLocationAllowed:
        settings?.devicePermissions?.emergencyLocationAllowed || false,
      volume: settings?.generalSettings?.volume || 50,
      functionalityMode:
        settings?.generalSettings?.functionalityMode?.currentValue || 1,
      language: settings?.generalSettings?.language?.currentValue || 1,
      profile: settings?.generalSettings?.profile?.currentValue || 1,
      timezone: settings?.generalSettings?.timezone?.currentValue || 1,
      userMode: settings?.generalSettings?.userMode?.currentValue || 1,
      statusUpdateInterval:
        settings?.generalSettings?.statusUpdateInterval?.currentValue || 360,
      //trackingEnabled: settings?.generalSettings?.trackingEnabled || false,
      answerMachineAvoidance:
        settings?.deviceCallList?.answerMachineAvoidance || false,
      ringingTimeout: settings?.deviceCallList?.ringingTimeout || 10,
      whitelistingEnabled:
        settings?.deviceCallList?.whitelistingEnabled || false,
      callCenterId: settings?.deviceCallList?.callCenterId || 0,
    },
    validationSchema: Yup.object({}),
    onSubmit: values => {
      let temp = new Object()
      temp.assisteeData = {}
      temp.assisteeData.firstName = values.firstName
      temp.assisteeData.lastName = values.lastName

      temp.companyData = {}
      temp.companyData.companyId = 711
      temp.companyData.groupId = 101619

      temp.devicePermissions = {}
      temp.devicePermissions.dataRetentionDays =
        values.dataRetentionDays === "number"
          ? values.dataRetentionDays
          : Number.parseInt(values.dataRetentionDays, 10)
      temp.devicePermissions.locationAllowed = values.locationAllowed
      temp.devicePermissions.emergencyLocationAllowed =
        values.emergencyLocationAllowed

      temp.generalSettings = {}
      temp.generalSettings.volume =
        values.volume === "number" ? values.volume : Number.parseInt(values.volume, 10)
      temp.generalSettings.functionalityMode =
        values.functionalityMode === "number"
          ? values.functionalityMode
          : Number.parseInt(values.functionalityMode, 10)
      temp.generalSettings.language =
        values.language === "number"
          ? values.language
          : Number.parseInt(values.language, 10)
      temp.generalSettings.profile =
        values.profile === "number"
          ? values.profile
          : Number.parseInt(values.profile, 10)
      temp.generalSettings.timezone =
        values.timezone === "number"
          ? values.timezone
          : Number.parseInt(values.timezone, 10)
      temp.generalSettings.statusUpdateInterval =
        values.statusUpdateInterval === "number"
          ? values.statusUpdateInterval
          : Number.parseInt(values.statusUpdateInterval, 10)
      temp.generalSettings.userMode =
        values.userMode === "number"
          ? values.userMode
          : Number.parseInt(values.userMode, 10)

      temp.deviceCallList = {}
      temp.deviceCallList.answerMachineAvoidance = values.answerMachineAvoidance
      temp.deviceCallList.ringingTimeout =
        values.ringingTimeout === "number"
          ? values.ringingTimeout
          : Number.parseInt(values.ringingTimeout, 10)
      temp.deviceCallList.whitelistingEnabled = values.whitelistingEnabled
      temp.deviceCallList.callCenterId =
        values.callCenterId === "number"
          ? values.callCenterId
          : Number.parseInt(values.callCenterId, 10)
      temp.deviceCallList.callList = callList
      temp.homeBeacons = homeBeacons

      temp.notifications = {}
      temp.notifications.manDown = {
        call: [{ receiver: "sosNumber", name: "", userId: 17821 }],
      }

      if (!updating) {
        setUpdating(true)
        setNavigilSettings(abortRef.current, props.deviceId, temp)
          .then(result => {
            if (result.statusCode == 200) {
              getSettings()
              showToast(props.t("Updated"), "success")
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            showToast(props.t("Error"), "error")
          })
          .finally(() => {
            setUpdating(false)
          })
      }
    },
  })

  // Beacon validation
  const beaconValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      address: "",
      mac: "",
      latitude: "61.489390",
      longitude: "23.780688",
    },
    validationSchema: Yup.object({
      name: Yup.string().required(props.t("Required field")),
      address: Yup.string().required(props.t("Required field")),
      mac: Yup.string()
        .matches(
          /^([0-9A-Fa-f]{2}:){5}([0-9A-Fa-f]{2})$/,
          props.t("Invalid format")
        )
        .required(props.t("Required field")),
      latitude: Yup.string()
        .required(props.t("Required field"))
        .test(
          "lat",
          "Invalid value",
          val => !Number.isNaN(val) && Math.abs(val) <= 90
        ),
      longitude: Yup.string()
        .required(props.t("Required field"))
        .test(
          "lng",
          "Invalid value",
          val => !Number.isNaN(val) && Math.abs(val) <= 180
        ),
    }),
    onSubmit: values => {
      console.log("New beacon: ", values)
      setHomeBeacons(prevState => [...prevState, values])
      setBeaconModal(false)
    },
  })

  // Contact validation
  const contactValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      number: "",
      email: "",
      helpRequest: true,
      hiddenContact: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(props.t("Required field")),
      number: Yup.string().required(props.t("Required field")),
    }),
    onSubmit: values => {
      console.log("New contact: ", values)
      setCallList(prevState => [...prevState, values])
      setContactModal(false)
    },
  })

  // Location changed in map
  const mapLocationChanged = latLng => {
    console.log("mapLocationChanged", latLng)
    beaconValidation.setFieldValue("latitude", latLng.lat.toFixed(6))
    beaconValidation.setFieldValue("longitude", latLng.lng.toFixed(6))
  }

  // Table structures
  const callColumns = [
    {
      dataIndex: "name",
      title: props.t("Name"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "number",
      title: props.t("Phone number"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "number", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "helpRequest",
      title: props.t("Help request"),
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "helpRequest", sortOrder)
      },
      render: value => (value ? props.t("Enabled") : props.t("Disabled")),
      width: "200px",
    },
    {
      dataIndex: "hiddenContact",
      title: props.t("Hidden contact"),
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "hiddenContact", sortOrder)
      },
      render: value => (value ? props.t("Yes") : props.t("No")),
      width: "200px",
    },
    {
      dataIndex: "actions",
      title: props.t("Actions"),
      align: "center",
      render: contactActionsFormatter,
      width: "145px",
    },
  ]

  function contactActionsFormatter(cell, row) {
    return (
      <>
        <Button
          color="danger"
          outline
          size="sm"
          onClick={() => {
            setCallList(prevState =>
              prevState.filter(prevItem => prevItem.name !== row.name)
            )
          }}
        >
          <i className="mdi mdi-trash-can" />
        </Button>
      </>
    )
  }

  const beaconColumns = [
    {
      dataIndex: "name",
      title: props.t("Name"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "mac",
      title: props.t("MAC"),
      defaultSortOrder: "ascend",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "mac", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "address",
      title: props.t("Address"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "address", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "latitude",
      title: props.t("Latitude"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "latitude", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "longitude",
      title: props.t("Longitude"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "longitude", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "actions",
      title: props.t("Actions"),
      align: "center",
      render: beaconActionsFormatter,
      width: "145px",
    },
  ]

  function beaconActionsFormatter(cell, row) {
    return (
      <>
        <Button
          color="danger"
          outline
          size="sm"
          onClick={() => {
            setHomeBeacons(prevState =>
              prevState.filter(prevItem => prevItem.mac !== row.mac)
            )
          }}
        >
          <i className="mdi mdi-trash-can" />
        </Button>
      </>
    )
  }

  const geofenceColumns = [
    {
      dataIndex: "geofenceAreaName",
      title: props.t("Name"),
      defaultSortOrder: "ascend",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "geofenceAreaName", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "type",
      title: props.t("Type"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "type", sortOrder)
      },
      width: "100px",
    },
    {
      dataIndex: "radius",
      title: props.t("Radius"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["radius"], b["radius"], sortOrder)
      },
      width: "100px",
    },
    {
      dataIndex: "coordinates",
      title: props.t("Coordinates"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "coordinates", sortOrder)
      },
      width: "1500px",
    },
  ]

  return (
    <>
      <div className="mb-3">
        <Button
          color="primary"
          disabled={loading || updating || !props.deviceId}
          onClick={() => {
            getSettings()
          }}
        >
          <i className="me-2 bx bxs-download text-truncate align-middle"></i>
          {props.t("Get settings")}
        </Button>

        <Button
          color="primary"
          className="ms-3"
          disabled={loading || updating || !settings}
          onClick={() => {
            setDefaultValues()
          }}
        >
          <i className="me-2 bx bxs-cog text-truncate align-middle"></i>
          {props.t("Set default values")}
        </Button>

        <Button
          color="success"
          className="ms-3"
          disabled={loading || updating || !settings}
          onClick={() => {
            validation.submitForm()
          }}
        >
          <i className="me-2 bx bxs-save text-truncate align-middle"></i>
          {props.t("Save changes")}
        </Button>

        {/** 
        <Button color="primary" className="ms-2" disabled>
          {props.t("Fetch ID")}
        </Button>*/}
      </div>

      {!props.deviceId && (
        <Alert color="danger">{props.t("Missing device ID")}</Alert>
      )}

      {loading && (
        <Lottie
          options={getLottieLoadingOptions()}
          height={100}
          width={100}
        ></Lottie>
      )}

      {!loading && !settings && (
        <Card>
          <CardBody>
            <div className="text-center mb-4">
              <Lottie
                options={getLottieNothingFound()}
                height={150}
                width={150}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {!loading && settings && (
        <Card>
          <CardBody className="p-0">
            <UncontrolledAccordion flush defaultOpen={["1"]} stayOpen>
              <AccordionItem>
                <AccordionHeader targetId="1">
                  {props.t("User")}
                </AccordionHeader>
                <AccordionBody
                  accordionId="1"
                  className="pt-0"
                  style={{ paddingTop: "0px" }}
                >
                  <Row xs={2} md={3}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("First name")}</Label>
                        <AntdInput
                          allowClear
                          placeholder={props.t("First name")}
                          value={validation.values.firstName}
                          onChange={(v) => { validation.setFieldValue("firstName", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Last name")}</Label>
                        <AntdInput
                          allowClear
                          placeholder={props.t("Last name")}
                          value={validation.values.lastName}
                          onChange={(v) => { validation.setFieldValue("lastName", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </AccordionBody>
              </AccordionItem>
            </UncontrolledAccordion>

            <hr className="m-0" />

            <UncontrolledAccordion flush defaultOpen={["2"]} stayOpen>
              <AccordionItem>
                <AccordionHeader targetId="2">
                  {props.t("Locationing info")}
                </AccordionHeader>
                <AccordionBody
                  accordionId="2"
                  className="pt-0"
                  style={{ paddingTop: "0px" }}
                >
                  <Row xs={2} md={3}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Data retention")}</Label>
                        <Select
                          value={"" + validation.values.dataRetentionDays}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("dataRetentionDays", v) }}
                          options={[
                            { label: "30 " + props.t("days"), value: "30" },
                            { label: "90 " + props.t("days"), value: "90" },
                            { label: "180 " + props.t("days"), value: "180" },
                            { label: "360 " + props.t("days"), value: "360" },
                            { label: "720 " + props.t("days"), value: "720" },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Location allowed")}</Label>
                        <Switch
                          className="d-block"
                          name="locationAllowed"
                          checked={validation.values.locationAllowed}
                          onChange={v => {
                            validation.setFieldValue("locationAllowed", v)
                          }}
                          checkedChildren={<i className="mdi mdi-check-bold" />}
                          unCheckedChildren={
                            <i className="mdi mdi-close-thick" />
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Emergency location allowed")}</Label>
                        <Switch
                          className="d-block"
                          name="emergencyLocationAllowed"
                          checked={validation.values.emergencyLocationAllowed}
                          onChange={v => {
                            validation.setFieldValue(
                              "emergencyLocationAllowed",
                              v
                            )
                          }}
                          checkedChildren={<i className="mdi mdi-check-bold" />}
                          unCheckedChildren={
                            <i className="mdi mdi-close-thick" />
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </AccordionBody>
              </AccordionItem>
            </UncontrolledAccordion>

            <hr className="m-0" />
            <UncontrolledAccordion flush defaultOpen={["3"]} stayOpen>
              <AccordionItem>
                <AccordionHeader targetId="3">
                  {props.t("General settings")}
                </AccordionHeader>
                <AccordionBody
                  accordionId="3"
                  className="pt-0"
                  style={{ paddingTop: "0px" }}
                >
                  <Row xs={2} md={3}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Functionality mode")}</Label>
                        <Select
                          value={validation.values.functionalityMode}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("functionalityMode", v) }}
                          options={funcModes.map(item => (
                            { label: props.t(item.langKey), value: item.id }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Profile")}</Label>
                        <Select
                          value={validation.values.profile}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("profile", v) }}
                          options={profiles.map(item => (
                            { label: props.t(item.profileName), value: item.id }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Language")}</Label>
                        <Select
                          value={validation.values.language}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("language", v) }}
                          options={languages.map(item => (
                            { label: props.t(item.langKey), value: item.id }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Timezone")}</Label>
                        <Select
                          value={validation.values.timezone}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("timezone", v) }}
                          options={timezones.map(item => (
                            { label: props.t(item.langKey), value: item.id }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Status update interval")}</Label>
                        <Select
                          value={validation.values.statusUpdateInterval}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("statusUpdateInterval", v) }}
                          options={updateIntervals.map(item => (
                            { label: item, value: item }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("User mode")}</Label>
                        <Select
                          value={validation.values.userMode}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("userMode", v) }}
                          options={userModes.map(item => (
                            { label: props.t(item.langKey), value: item.id }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>{props.t("Volume")}</Label>
                    <div className="pb-2 px-2" style={{ maxWidth: "600px" }}>
                      <Box sx={{ width: "100%" }}>
                        <Slider
                          min={1}
                          max={10}
                          marks={{
                            1: { label: "1" },
                            10: { label: "10" },
                          }}
                          value={validation.values.volume}
                          onChange={value => {
                            validation.setFieldValue("volume", value)
                          }}
                        />
                      </Box>
                    </div>
                  </FormGroup>
                </AccordionBody>
              </AccordionItem>
            </UncontrolledAccordion>

            <hr className="m-0" />
            <UncontrolledAccordion flush defaultOpen={["4"]} stayOpen>
              <AccordionItem>
                <AccordionHeader targetId="4">
                  {props.t("Contacts")}
                </AccordionHeader>
                <AccordionBody
                  accordionId="4"
                  className="pt-0"
                  style={{ paddingTop: "0px" }}
                >
                  <Row xs={2} md={3}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Ringing timeout")}</Label>
                        <Select
                          value={"" + validation.values.ringingTimeout}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("ringingTimeout", v) }}
                          options={[
                            { label: "20s", value: "20" },
                            { label: "30s", value: "30" },
                            { label: "60s", value: "60" },
                            { label: "90s", value: "90" },
                            { label: "120s", value: "120" },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Answer machine avoidance")}</Label>
                        <Switch
                          className="d-block"
                          name="answerMachineAvoidance"
                          checked={validation.values.answerMachineAvoidance}
                          onChange={v => {
                            validation.setFieldValue(
                              "answerMachineAvoidance",
                              v
                            )
                          }}
                          checkedChildren={<i className="mdi mdi-check-bold" />}
                          unCheckedChildren={
                            <i className="mdi mdi-close-thick" />
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Whitelisting enabled")}</Label>
                        <Switch
                          className="d-block"
                          name="whitelistingEnabled"
                          checked={validation.values.whitelistingEnabled}
                          onChange={v => {
                            validation.setFieldValue("whitelistingEnabled", v)
                          }}
                          checkedChildren={<i className="mdi mdi-check-bold" />}
                          unCheckedChildren={
                            <i className="mdi mdi-close-thick" />
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Call center")}</Label>
                        <Select
                          value={validation.values.callCenterId}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("callCenterId", v) }}
                          options={callCenters.map(item => (
                            { label: (item.name + ": " + item.number), value: item.id }
                          ))}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <React.Fragment>
                    <Row className="mb-1">
                      <Col className="col-auto">
                        <Button
                          size="sm"
                          style={{ borderRadius: "100px" }}
                          color="success"
                          onClick={() => {
                            setContactModal(true)
                          }}
                        >
                          <i className="mdi mdi-plus me-2" />
                          {props.t("Add")}
                        </Button>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col xl="12">
                        <AntdTable
                          className="clear-header"
                          bordered={true}
                          size="middle"
                          sticky
                          rowKey={"id"}
                          columns={callColumns}
                          dataSource={callList}
                          pagination={{
                            showSizeChanger: true,
                            defaultPageSize: 50,
                            pageSizeOptions: defaultPageSizeOptions,
                          }}
                          tableLayout="auto"
                          scroll={{
                            x: "max-content",
                            y: tableDefaultHeight,
                          }}
                        />
                      </Col>
                    </Row>
                  </React.Fragment>
                </AccordionBody>
              </AccordionItem>
            </UncontrolledAccordion>

            <hr className="m-0" />
            <UncontrolledAccordion flush defaultOpen={["5"]} stayOpen>
              <AccordionItem>
                <AccordionHeader targetId="5">
                  {props.t("Home beacons")}
                </AccordionHeader>
                <AccordionBody
                  accordionId="5"
                  className="pt-0"
                  style={{ paddingTop: "0px" }}
                >
                  <React.Fragment>
                    <Row className="mb-1">
                      <Col className="col-auto">
                        <Button
                          size="sm"
                          style={{ borderRadius: "100px" }}
                          color="success"
                          onClick={() => {
                            setBeaconModal(true)
                          }}
                        >
                          <i className="mdi mdi-plus me-2" />
                          {props.t("Add")}
                        </Button>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col xl="12">
                        <div className="table-responsive">
                          <AntdTable
                            className="clear-header"
                            bordered={true}
                            size="middle"
                            sticky
                            rowKey={"id"}
                            columns={beaconColumns}
                            dataSource={homeBeacons}
                            pagination={{
                              showSizeChanger: true,
                              defaultPageSize: 50,
                              pageSizeOptions: defaultPageSizeOptions,
                            }}
                            tableLayout="auto"
                            scroll={{
                              x: "max-content",
                              y: tableDefaultHeight,
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </React.Fragment>
                </AccordionBody>
              </AccordionItem>
            </UncontrolledAccordion>

            <hr className="m-0" />
            <UncontrolledAccordion flush defaultOpen={["6"]} stayOpen>
              <AccordionItem>
                <AccordionHeader targetId="6">
                  {props.t("Geofences")}
                </AccordionHeader>
                <AccordionBody
                  accordionId="6"
                  className="pt-0"
                  style={{ paddingTop: "0px" }}
                >
                  <React.Fragment>
                    <Row className="mt-2">
                      <Col xl="12">
                        <div className="table-responsive">
                          <AntdTable
                            className="clear-header"
                            bordered={true}
                            size="middle"
                            sticky
                            rowKey={"id"}
                            columns={geofenceColumns}
                            dataSource={geofences}
                            pagination={{
                              showSizeChanger: true,
                              defaultPageSize: 50,
                              pageSizeOptions: defaultPageSizeOptions,
                            }}
                            tableLayout="auto"
                            scroll={{
                              x: "max-content",
                              y: tableDefaultHeight,
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </React.Fragment>
                </AccordionBody>
              </AccordionItem>
            </UncontrolledAccordion>
          </CardBody>
        </Card>
      )}

      <Modal
        isOpen={contactModal}
        toggle={() => {
          setContactModal(!contactModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Contact")}</ModalHeader>
        <ModalBody>
          <Row xs={1} lg={2}>
            <Col>
              <FormGroup>
                <Label>{props.t("Name")}</Label>
                <AntdInput
                  allowClear
                  placeholder={"Maija Meikäläinen"}
                  value={contactValidation.values.name}
                  onChange={(v) => { contactValidation.setFieldValue("name", v.target.value) }}
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label>{props.t("Number")}</Label>
                <AntdInput
                  allowClear
                  placeholder={"358501234567"}
                  value={contactValidation.values.number}
                  onChange={(v) => { contactValidation.setFieldValue("number", v.target.value) }}
                />
              </FormGroup>
            </Col>
            {/** 
            <Col>
              <FormGroup>
                <Label>{props.t("Email")}</Label>
                <Input
                  bsSize="sm"
                  name="email"
                  type="text"
                  placeholder="email@example.com"
                  value={contactValidation.values.email}
                  onChange={contactValidation.handleChange}
                />
              </FormGroup>
            </Col>
            */}
            <Col>
              <FormGroup>
                <Label>{props.t("Help request")}</Label>
                <Switch
                  className="d-block"
                  name="helpRequest"
                  checked={contactValidation.values.helpRequest}
                  onChange={v => {
                    contactValidation.setFieldValue("helpRequest", v)
                  }}
                  checkedChildren={<i className="mdi mdi-check-bold" />}
                  unCheckedChildren={<i className="mdi mdi-close-thick" />}
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label>{props.t("Hidden contact")}</Label>
                <Switch
                  className="d-block"
                  name="hiddenContact"
                  checked={contactValidation.values.hiddenContact}
                  onChange={v => {
                    contactValidation.setFieldValue("hiddenContact", v)
                  }}
                  checkedChildren={<i className="mdi mdi-check-bold" />}
                  unCheckedChildren={<i className="mdi mdi-close-thick" />}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => {
              setContactModal(false)
            }}
          >
            {props.t("Cancel")}
          </Button>
          <Button
            color="success"
            onClick={() => {
              contactValidation.submitForm()
            }}
          >
            {props.t("Add")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={beaconModal}
        toggle={() => {
          setBeaconModal(!beaconModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Home beacon")}</ModalHeader>
        <ModalBody>
          <Row xs={1} lg={2}>
            <Col>
              <FormGroup>
                <Label>{props.t("Name")}</Label>
                <AntdForm.Item validateStatus={beaconValidation.errors.name ? "error" : ""} hasFeedback help={beaconValidation.errors.name}>
                  <AntdInput
                    allowClear
                    placeholder={"Olohuone"}
                    value={beaconValidation.values.name}
                    onChange={(v) => { beaconValidation.setFieldValue("name", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label>{props.t("MAC")}</Label>
                <AntdForm.Item validateStatus={beaconValidation.errors.mac ? "error" : ""} hasFeedback help={beaconValidation.errors.mac}>
                  <AntdInput
                    allowClear
                    placeholder={"11:22:33:AA:BB:0F"}
                    value={beaconValidation.values.mac}
                    onChange={(v) => { beaconValidation.setFieldValue("mac", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label>{props.t("Address")}</Label>
                <AntdForm.Item validateStatus={beaconValidation.errors.address ? "error" : ""} hasFeedback help={beaconValidation.errors.address}>
                  <AntdInput
                    allowClear
                    placeholder={"Tivolikuja 1"}
                    value={beaconValidation.values.address}
                    onChange={(v) => { beaconValidation.setFieldValue("address", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
            </Col>
            <Col style={{ alignSelf: "center" }}>
              <Button
                size="sm"
                style={{ width: "100%" }}
                color="primary"
                disabled={fetching || !beaconValidation.values.address}
                onClick={() => {
                  getCoordinates(beaconValidation.values.address)
                }}
              >
                {props.t("Get coordinates")}
              </Button>
            </Col>
            <Col>
              <FormGroup>
                <Label>{props.t("Latitude")}</Label>
                <AntdForm.Item validateStatus={beaconValidation.errors.latitude ? "error" : ""} hasFeedback help={beaconValidation.errors.latitude}>
                  <AntdInput
                    allowClear
                    placeholder={"60.188209"}
                    value={beaconValidation.values.latitude}
                    onChange={(v) => { beaconValidation.setFieldValue("latitude", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label>{props.t("Longitude")}</Label>
                <AntdForm.Item validateStatus={beaconValidation.errors.longitude ? "error" : ""} hasFeedback help={beaconValidation.errors.longitude}>
                  <AntdInput
                    allowClear
                    placeholder={"24.940707"}
                    value={beaconValidation.values.longitude}
                    onChange={(v) => { beaconValidation.setFieldValue("longitude", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
            </Col>
          </Row>
          <Row style={{ height: "260px" }}>
            <Col>
              <DefaultMap
                zoom={10}
                lat={beaconValidation.values.latitude}
                lng={beaconValidation.values.longitude}
                infoHeader={props.t("Address")}
                infoText={beaconValidation.values.address}
                markerEdit={true}
                markerChanged={mapLocationChanged}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => {
              setBeaconModal(false)
            }}
          >
            {props.t("Cancel")}
          </Button>
          <Button
            color="success"
            onClick={() => {
              beaconValidation.submitForm()
            }}
          >
            {props.t("Add")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

NavigilSettings.propTypes = {
  t: PropTypes.any,
  deviceId: PropTypes.string,
}

export default withRouter(withTranslation()(NavigilSettings))
